import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import styles from './ConversionBar.module.scss';
import { getHighLevelStatus, HighLevelStatus, shouldTreatAsLiveEvent } from '@/utils/highLevelStatus';
import { useDisplayStatus } from '../_shared/contexts/DisplayStatusContext';
import { LiveEventPanelInfo } from './LiveEvent/LiveEventPanelInfo';
import { GeneralEventPanelInfo } from './GeneralEventPanelInfo/GeneralEventPanelInfo';
import { DiscountSignal } from './DiscountSignal/DiscountSignal';
import { useBasicInfo } from '../_shared/contexts/BasicInfoContext';
import { useShouldDisplayRsvp } from './hooks/useShouldDisplayRsvp';
import { RSVPPanel } from './RSVP/RSVPPanel';
import { ExternalTicketsPanel } from './ExternalTicketsPanel/ExternalTicketsPanel';
import { FormattedMessage } from 'react-intl';
import { EarlyAccessStrip } from './GeneralEventPanelInfo/SalesStartSoonPanel/EarlyAccessStrip';
import { useEarlyAccessState } from './GeneralEventPanelInfo/SalesStartSoonPanel/useEarlyAccessState';

type ConversionBarProps = {
  isPositionCalculated?: boolean;
};

export const ConversionBar: React.FC<ConversionBarProps> = ({ isPositionCalculated = true }) => {
  const wrapperClasses = classNames(styles.wrapper, {
    [styles['hidden']]: !isPositionCalculated,
  });
  const [isDiscountSignalVisible, setIsDiscountSignalVisible] = useState(false);

  const { shouldDisplayRsvp, mipaToken } = useShouldDisplayRsvp();
  const { displayStatus, salesStatusData } = useDisplayStatus();
  const { phase: earlyAccessPhase } = useEarlyAccessState();

  const { endDate, hasDonationTicketsAvailable = false, externalTicketing } = useBasicInfo();
  const externalTicketsAreAvailable = externalTicketing?.externalTicketsAreAvailable ?? false;
  const isLiveEvent = shouldTreatAsLiveEvent(displayStatus, endDate?.utc, hasDonationTicketsAvailable);

  // Keep this behavior sales-driven: donations grace panel should depend on sales status,
  // even when lifecycle status (ended/completed) wins displayStatus precedence.
  const salesDerivedStatus = getHighLevelStatus(salesStatusData);
  const isSalesEndedWithDonations = salesDerivedStatus === HighLevelStatus.SALES_ENDED && hasDonationTicketsAvailable;
  const canShowDiscountSignal = displayStatus !== HighLevelStatus.SOLD_OUT;

  const infoPanel = useMemo(() => {
    if (shouldDisplayRsvp) {
      return <RSVPPanel mipaToken={mipaToken} />;
    }
    if (externalTicketsAreAvailable) {
      return <ExternalTicketsPanel />;
    }
    // While the Early Access window is open, a member gets the on-sale panel; the CTA lifts only the
    // not-yet-on-sale suppression, and every other safety gate still applies.
    if (earlyAccessPhase === 'open') {
      return <LiveEventPanelInfo showDiscountSignal={setIsDiscountSignalVisible} isEarlyAccessWindowOpen />;
    }
    return isLiveEvent && !isSalesEndedWithDonations ? (
      <LiveEventPanelInfo showDiscountSignal={setIsDiscountSignalVisible} />
    ) : (
      <GeneralEventPanelInfo showSalesEndedDonationPanel={isSalesEndedWithDonations && isLiveEvent} />
    );
  }, [
    shouldDisplayRsvp,
    mipaToken,
    externalTicketsAreAvailable,
    earlyAccessPhase,
    isLiveEvent,
    isSalesEndedWithDonations,
  ]);

  return (
    <div className={wrapperClasses} data-testid="conversion-bar">
      {displayStatus === HighLevelStatus.SOLD_OUT && (
        <span className={styles.soldOutSignal} data-testid="conversion-bar-sold-out-signal">
          <FormattedMessage
            id="Tickets Sold Out"
            defaultMessage="Tickets Sold Out"
            description="Sold out signal shown above the conversion bar"
          />
        </span>
      )}
      {canShowDiscountSignal && isDiscountSignalVisible && <DiscountSignal />}
      <div className={styles.content}>
        <div className={styles.infoPanelWrapper} data-testid="conversion-bar-panel-info">
          {infoPanel}
        </div>
      </div>
      {earlyAccessPhase !== 'none' && !shouldDisplayRsvp && !externalTicketsAreAvailable && (
        <EarlyAccessStrip phase={earlyAccessPhase} />
      )}
    </div>
  );
};
